import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Stack,
  Chip,
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Receipt as InvoiceIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface SuccessModalProps {
  open: boolean;
  onClose: () => void;
  invoiceId: number | null;
  expenseInvoiceId?: number | null;
  invoiceNumber: string | null;
  message: string | null;
}

/**
 * Success modal displayed after successful import approval
 * Shows invoice details and navigation options
 */
const SuccessModal: React.FC<SuccessModalProps> = ({
  open,
  onClose,
  invoiceId,
  expenseInvoiceId,
  invoiceNumber,
  message,
}) => {
  const navigate = useNavigate();

  const handleViewInvoice = () => {
    console.log('[SuccessModal] handleViewInvoice called', { expenseInvoiceId, invoiceId });
    // Priority 1: Expense invoice
    if (expenseInvoiceId) {
      console.log('[SuccessModal] Navigating to expense invoice:', expenseInvoiceId);
      navigate(`/expense-invoices/${expenseInvoiceId}`);
      onClose();
    }
    // Priority 2: Sales invoice
    else if (invoiceId) {
      console.log('[SuccessModal] Navigating to sales invoice:', invoiceId);
      navigate(`/invoices/${invoiceId}`);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={2} alignItems="center">
          <SuccessIcon sx={{ color: 'success.main', fontSize: 40 }} />
          <Typography variant="h5">Import zatwierdzony!</Typography>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="success" sx={{ mb: 3 }}>
          {message || 'Import został pomyślnie zatwierdzony i faktura została utworzona.'}
        </Alert>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Utworzona faktura:
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <InvoiceIcon color="primary" />
            <Chip
              label={invoiceNumber || `ID: ${invoiceId}`}
              color="primary"
              variant="outlined"
              size="medium"
            />
          </Stack>
        </Box>

        {(invoiceId || expenseInvoiceId) && (
          <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Możesz teraz przejść do szczegółów faktury lub zamknąć to okno i kontynuować pracę.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Zamknij</Button>
        {(invoiceId || expenseInvoiceId) && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<ViewIcon />}
            onClick={handleViewInvoice}
          >
            Zobacz fakturę
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SuccessModal;
